import AuthService from './auth.js';
import { authenticatedFetch } from '../utils.js';

class CompanyService {
  constructor() {
    // This could be useful if you need to access authentication methods
    this.authService = AuthService;
  }

  /**
   * Helper to perform an API request with error handling.
   *
   * @param {string} url - The API endpoint URL.
   * @param {object} options - Fetch options.
   * @param {string} defaultErrorMessage - Default error message if the response does not include one.
   * @returns {Promise<object>} - The parsed JSON data from the response.
   */
  async _handleRequest(url, options, defaultErrorMessage) {
    try {
      const response = await authenticatedFetch(url, options);
      // Try to parse JSON; if parsing fails, default to an empty object.
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || defaultErrorMessage);
      }
      return data;
    } catch (error) {
      console.error(`Error in request to ${url}:`, error);
      throw error;
    }
  }

  getCompanies() {
    return this._handleRequest(
      '/api/companies/',
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      },
      'Failed to fetch companies'
    );
  }

  getCompany(id) {
    return this._handleRequest(
      `/api/companies/${id}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      },
      'Failed to fetch company'
    );
  }

  createCompany(companyData) {
    return this._handleRequest(
      '/api/companies/',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData)
      },
      'Failed to create company'
    );
  }

  updateCompany(id, companyData) {
    return this._handleRequest(
      `/api/companies/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData)
      },
      'Failed to update company'
    );
  }

  async deleteCompany(id) {
    await this._handleRequest(
      `/api/companies/${id}`,
      { method: 'DELETE' },
      'Failed to delete company'
    );
    return true;
  }

  createTemplate(companyId, templateData) {
    return this._handleRequest(
      `/api/companies/${companyId}/templates`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      },
      'Failed to create template'
    );
  }

  updateTemplate(companyId, templateId, templateData) {
    return this._handleRequest(
      `/api/companies/${companyId}/templates/${templateId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      },
      'Failed to update template'
    );
  }

  async deleteTemplate(companyId, templateId) {
    await this._handleRequest(
      `/api/companies/${companyId}/templates/${templateId}`,
      { method: 'DELETE' },
      'Failed to delete template'
    );
    return true;
  }

  updatePayment(companyId, paymentData) {
    return this._handleRequest(
      `/api/companies/${companyId}/payment`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentData)
      },
      'Failed to update payment'
    );
  }

  getMessageStatistics(companyId, period = null) {
    const url = new URL(`/api/companies/${companyId}/statistics`, window.location.origin);
    if (period) {
      url.searchParams.append('period', period);
    }
    return this._handleRequest(
      url.toString(),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      },
      'Failed to fetch message statistics'
    );
  }

  toggleTemplate(companyId, templateId) {
    return this._handleRequest(
      `/api/companies/${companyId}/templates/${templateId}/toggle`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      },
      'Failed to toggle template'
    );
  }

  resetTemplates(companyId) {
    return this._handleRequest(
      `/api/companies/${companyId}/templates/reset`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      },
      'Failed to reset templates'
    );
  }

  toggleTestMode(companyId) {
    return this._handleRequest(
      `/api/companies/${companyId}/test-mode/toggle`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      },
      'Failed to toggle test mode'
    );
  }
}

export default new CompanyService();
